import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePlatformOperations } from "@/api/queries/processes";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { getOperationTone } from "@/lib/operationTone";
import { buildTenantUrl } from "@/shell/resolveShell";

import type { PlatformOperation } from "@/api/types";

const SKELETON_COUNT = 3;

/**
 * A category earns a folded card only once it would otherwise crowd out
 * everything else. Two operations read as two things a shop can price; eight
 * surface finishes read as "this is a tool for a paint shop" — the wrong
 * conclusion from a true list (KAN-1115).
 */
const FOLD_FROM = 3;

/** One rendered row of the list: a single operation, or a folded category. */
export type SegmentEntry =
  | { kind: "operation"; process: PlatformOperation }
  | {
      kind: "category";
      slug: string;
      label: string;
      description: string | null;
      color: string | null;
      processes: PlatformOperation[];
    };

/**
 * Group the operation list by category, keeping the order the API sent
 * (``landing_order``): a group takes the position of its first member, so the
 * admin's ordering still decides what the visitor reads first.
 *
 * Operations with no category — and categories the platform has not named —
 * stay ungrouped, because a card headed by a bare slug would say less than the
 * operations it hides.
 */
export function groupByCategory(
  processes: PlatformOperation[],
  lang: string,
  foldFrom = FOLD_FROM,
): SegmentEntry[] {
  const byCategory = new Map<string, PlatformOperation[]>();
  for (const process of processes) {
    const slug = process.operation_category;
    if (!slug || !process.operation_category_label) continue;
    const bucket = byCategory.get(slug);
    if (bucket) bucket.push(process);
    else byCategory.set(slug, [process]);
  }

  const emitted = new Set<string>();
  const entries: SegmentEntry[] = [];

  for (const process of processes) {
    const slug = process.operation_category;
    const siblings = slug ? byCategory.get(slug) : undefined;
    if (!slug || !siblings || siblings.length < foldFrom) {
      entries.push({ kind: "operation", process });
      continue;
    }
    if (emitted.has(slug)) continue;
    emitted.add(slug);
    entries.push({
      kind: "category",
      slug,
      label: pickI18nBlob(
        process.operation_category_label_i18n,
        process.operation_category_label ?? "",
        process.operation_category_label_en ?? "",
        lang,
      ),
      // Only what an admin actually wrote: no description ⇒ the card carries
      // its name and its count, never invented prose.
      description:
        pickI18nBlob(
          process.operation_category_description_i18n,
          process.operation_category_description ?? "",
          process.operation_category_description_en ?? "",
          lang,
        ) || null,
      color: process.operation_category_color ?? null,
      processes: siblings,
    });
  }

  return entries;
}

/**
 * The one block on the marketing site whose content is live product
 * data: the operations list comes from ``GET /processes``, which on the
 * apex returns the modules the platform published to everyone.
 *
 * That is also why the proof lives here rather than in a separate
 * "proof" section. The count is derived from the same array the list
 * renders — it cannot drift from what the visitor sees below it, the
 * way a hand-written number would — and the demo link goes to a shop
 * where those exact operations can be tried. Claim, evidence and a way
 * to check, in one place.
 *
 * Folding is presentation only: every operation stays one click away and the
 * total underneath still counts them all. Nothing is dropped, it is filed.
 *
 * Card tints are the category colour the platform admin picked (owner
 * decision, KAN-1329), painted in the design's gradient shape.
 */
export function PlatformSegments({ lang }: { lang: string }): JSX.Element | null {
  const { t, i18n } = useTranslation("common");
  const { data: processes, isLoading, isError } = usePlatformOperations();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const entries = useMemo(
    () => groupByCategory(processes ?? [], i18n.language),
    [processes, i18n.language],
  );

  if (isError) return null;
  if (!isLoading && (!processes || processes.length === 0)) return null;

  const liveCount = processes?.length ?? 0;

  return (
    <section className="section alt" id="operace">
      <div className="inner">
        <div className="head-row">
          <div>
            <p className="kicker">{t("platformLanding.segments.kicker")}</p>
            <h2 className="h2" style={{ maxWidth: 520 }}>
              {t("platformLanding.segments.heading")}
            </h2>
          </div>
          <p className="lead">{t("platformLanding.segments.lead")}</p>
        </div>

        <ul className="ops" data-testid="platform-segments">
          {isLoading
            ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <li key={`skeleton-${i}`} className="op skeleton" aria-hidden="true">
                  <span />
                </li>
              ))
            : entries.map((entry) =>
                entry.kind === "operation" ? (
                  <OperationCard
                    key={entry.process.slug}
                    process={entry.process}
                  />
                ) : (
                  <CategoryCard
                    key={entry.slug}
                    entry={entry}
                    open={Boolean(expanded[entry.slug])}
                    onToggle={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [entry.slug]: !prev[entry.slug],
                      }))
                    }
                    countLabel={t("platformLanding.segments.categoryCount", {
                      count: entry.processes.length,
                    })}
                    toggleLabel={t(
                      expanded[entry.slug]
                        ? "platformLanding.segments.categoryCollapse"
                        : "platformLanding.segments.categoryExpand",
                    )}
                  />
                ),
              )}
        </ul>

        {/* Both halves are gated on a real list: the demo link would
            otherwise sit above an empty section, and the count would be a
            number with nothing behind it. The cross-host link is on
            purpose — the demo shop is a real tenant on its own subdomain,
            not a mock-up rendered here.

            buildtenanturl-ok: public storefront. The visitor is anonymous —
            there is no session to carry, and signing them in would be wrong
            for a "look at the demo shop" link. */}
        {liveCount > 0 && (
          <div className="ops-foot">
            <div>
              <a
                href={buildTenantUrl(`/${lang}`)}
                target="_blank"
                rel="noreferrer"
                className="ghost-btn"
                data-testid="platform-segments-demo-link"
              >
                {t("platformLanding.segments.demoCta")}
                <span aria-hidden="true">→</span>
              </a>
            </div>
            <p className="ops-note" data-testid="platform-segments-live-count">
              {t("platformLanding.segments.liveCount", { count: liveCount })}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/** The category hue, read by ``.pl .op`` in PlatformLanding.css. */
function toneStyle(color: string | null | undefined) {
  return { "--op-tint": getOperationTone(color ?? undefined).dot } as React.CSSProperties;
}

function OperationCard({ process }: { process: PlatformOperation }): JSX.Element {
  return (
    <li
      className="op"
      style={toneStyle(process.operation_category_color)}
      data-testid={`platform-segment-${process.slug}`}
    >
      <h3>{process.display_name}</h3>
      <p>{process.description}</p>
      {process.accepted_file_types.length > 0 && (
        <div className="op-tags">
          {process.accepted_file_types.map((ext) => (
            <span key={ext} className="tag">
              {ext.replace(/^\./, "").toUpperCase()}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function CategoryCard({
  entry,
  open,
  onToggle,
  countLabel,
  toggleLabel,
}: {
  entry: Extract<SegmentEntry, { kind: "category" }>;
  open: boolean;
  onToggle: () => void;
  countLabel: string;
  toggleLabel: string;
}): JSX.Element {
  return (
    <li
      className="op"
      style={toneStyle(entry.color)}
      data-testid={`platform-segment-category-${entry.slug}`}
    >
      <div className="op-head">
        <h3>{entry.label}</h3>
        <span
          className="op-count"
          data-testid={`platform-segment-count-${entry.slug}`}
        >
          {countLabel}
        </span>
      </div>
      {entry.description && <p>{entry.description}</p>}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="op-btn"
        data-testid={`platform-segment-toggle-${entry.slug}`}
      >
        {toggleLabel} <span aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <ul
          className="op-children"
          data-testid={`platform-segment-children-${entry.slug}`}
        >
          {entry.processes.map((process) => (
            <li key={process.slug}>
              <h4>{process.display_name}</h4>
              <p>{process.description}</p>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
